<template>
    <div class="StatisticsMarketingDatabase">
        <v-row class="pa-4 pb-0" v-if="!prefilter">
            <template v-for="(filter, index) in filters">
                <vDaterangePicker v-if="filter.type == 'dateperiod'" mode="past" v-model="filter.value" @change="reloadData" class="mr-6" :label="index" />
                <v-autocomplete v-else-if="filter.type == 'select'" :no-data-text="$t('noData')" multiple clearable v-model="filter.value" class="mr-6 mt-0 pt-0" @change="reloadData" :items="filter.items" style="max-width: 400px;" :label="$t(index)" />
                <v-switch v-else-if="filter.type == 'toggle' && filters.entity.value.length" v-model="filter.value" class="mr-6 mt-0 pt-0" @change="reloadData" :label="$t(index)" />
            </template>
        </v-row>

        <!-- KPIs Cards -->
        <v-row class="mt-0">
            <v-col cols="3" v-for="(kpi, index) in kpiData" :key="'kpi_' + index">
                <v-card class="kpi-card" :class="kpi.gradient ? 'gradient-card' : ''" elevation="2">
                    <v-card-text class="pa-4">
                        <div class="kpi-content">
                            <!-- Titre en haut avec ou sans icône info -->
                            <div class="kpi-title-container">
                                <span class="kpi-title">{{ $t(kpi.label) }}</span>
                                <infoTooltip v-if="kpi.tooltip" :tooltip="kpi.tooltip" />
                            </div>
                            
                            <!-- Nombre au centre -->
                            <div class="kpi-number">
                                <h2 class="kpi-value">{{ kpi.value }}</h2>
                            </div>
                            
                            <!-- Coût en vert en dessous -->
                            <div class="kpi-cost">
                                <span class="kpi-cost-text">{{ kpi.metric }}</span>
                            </div>
                            
                            <!-- Icône dans cercle bleu clair à droite -->
                            <div class="kpi-icon-container">
                                <div class="kpi-icon-container-inner">
                                    <v-icon :color="kpi.iconColor" size="24">{{ kpi.icon }}</v-icon>
                                    <div class="kpi-icon-circle"></div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="6" class="d-flex">
                <v-card class="pa-3 pt-0 fill-height-2" elevation="2">
                    <v-card-title>
                        {{ $t('localisation') }}
                    </v-card-title>
                    <v-card-text class="chart-container">
                        <heatMapComponent :data="dataHeatMap" v-if="(loadData || mode == 'dialog') && !$store.state.loaderDashboard" style="z-index:4"/>    
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="6" class="d-flex">
                <v-card class="pa-3 pt-0 fill-height-2" elevation="2">
                    <v-card-title>
                        {{ $t('consents_by_channels') }}
                    </v-card-title>
                    <v-card-text>
                        <VerticalBarStackedComponent :dataSet="optinData" v-if="(loadData || mode == 'dialog') && !$store.state.loaderDashboard" :legend="true"  :key="graphKey"/>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <!-- Type de leads - Horizontal Bar Chart -->
            <v-col cols="3" class="d-flex flex-column">
                <v-card class="pa-3 pt-0 fill-height" elevation="2">
                    <v-card-title>
                        {{ $t('joignability') }}
                        <infoTooltip v-if="joignabilityData.tooltip" :tooltip="joignabilityData.tooltip" />
                    </v-card-title>
                    <v-card-text style="height:calc(100% - 55px); display:flex; flex-direction: column;">
                        <div v-for="(item, index) in joignabilityData.labels" :key="'lead-type-' + index" class="mb-4">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="font-weight-medium">{{ item }}</span>
                                <span class="text-caption" v-if ="getJoignabilityTotal() > 0">{{ Math.round((joignabilityData.datasets[0].data[index] / getJoignabilityTotal()) * 100) }}% 
                                    <span class="success--text" v-if="joignabilityData.datasets[0].externalData && joignabilityData.datasets[0].externalData[index] > 0">
                                        <span v-if="Math.round(((joignabilityData.datasets[0].externalData[index]) / getJoignabilityTotal()) * 100) > 0">+{{Math.round(((joignabilityData.datasets[0].externalData[index]) / getJoignabilityTotal()) * 100)}} %</span>
                                        <span v-else>< 1 %</span>
                                    </span>
                                </span>
                                <span class="text-caption" v-else>0 %</span>
                            </div>
                            <v-progress-linear 
                                v-if="joignabilityData.datasets[0].externalData" 
                                rounded class="grey lighten-2" 
                                background-color="success" 
                                    height="8"
                                :buffer-value="( getJoignabilityTotal() > 0 ? (((joignabilityData.datasets[0].data[index] + joignabilityData.datasets[0].externalData[index]) / getJoignabilityTotal()) * 100) : 0)" 
                                :value="( getJoignabilityTotal()> 0 ? ((joignabilityData.datasets[0].data[index] / getJoignabilityTotal()) * 100) : 0)">
                            </v-progress-linear>
                            <v-progress-linear
                                v-else
                                :value="( getJoignabilityTotal() > 0 ? ((joignabilityData.datasets[0].data[index] / getJoignabilityTotal()) * 100) : 0)"
                                height="8"
                                color="primary"
                                background-color="grey lighten-3"
                                rounded
                            ></v-progress-linear>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Type de leads - Horizontal Bar Chart -->
            <v-col cols="3" class="d-flex flex-column">
                <v-card class="pa-3 pt-0 fill-height" elevation="2">
                    <v-card-title>
                        {{ $t('marketing_pressure') }}
                        <infoTooltip v-if="marketingPressureData.tooltip" :tooltip="marketingPressureData.tooltip" />
                    </v-card-title>
                    <v-card-text style="height:calc(100% - 55px); display:flex; flex-direction: column;">
                        <div v-for="(item, index) in marketingPressureData.labels" :key="'lead-type-' + index" class="mb-4">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="font-weight-medium">{{ item }}</span>
                                <span class="text-caption" v-if="getMarketingPressureTotal() > 0">{{ marketingPressureData.datasets[0].data[index] }} | {{ Math.round((marketingPressureData.datasets[0].data[index] / getMarketingPressureTotal()) * 100) }}%</span>
                                <span class="text-caption" v-else> 0 | 0 %</span>
                            </div>
                            <v-progress-linear
                                :value="(getMarketingPressureTotal() > 0 ? ((marketingPressureData.datasets[0].data[index] / getMarketingPressureTotal()) * 100) : 0)"
                                height="8"
                                color="primary"
                                background-color="grey lighten-3"
                                rounded
                            ></v-progress-linear>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Type de leads - Horizontal Bar Chart -->
            <v-col cols="3" class="d-flex flex-column">
                <v-card class="pa-3 pt-0 fill-height" elevation="2">
                    <v-card-title>
                        {{ $t('b2b_b2c') }}
                    </v-card-title>
                    <v-card-text style="height:calc(100% - 55px); display:flex; flex-direction: column;">
                        <div v-for="(item, index) in b2bB2cData.labels" :key="'lead-type-' + index" class="mb-4">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="font-weight-medium">{{ item }}</span>
                                <span class="text-caption" v-if="getB2bB2cTotal() > 0">{{ b2bB2cData.datasets[0].data[index] }} | {{ Math.round((b2bB2cData.datasets[0].data[index] / getB2bB2cTotal()) * 100) }}%</span>
                                <span class="text-caption" v-else>0 | 0 %</span>
                            </div>
                            <v-progress-linear
                                :value="( getB2bB2cTotal() > 0 ? ((b2bB2cData.datasets[0].data[index] / getB2bB2cTotal()) * 100) : 0)"
                                height="8"
                                color="primary"
                                background-color="grey lighten-3"
                                rounded
                            ></v-progress-linear>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Type de leads - Horizontal Bar Chart -->
            <v-col cols="3" class="d-flex flex-column">
                <v-card class="pa-3 pt-0 fill-height" elevation="2">
                    <v-card-title>
                        {{ $t('last_activity') }}
                        <infoTooltip v-if="lastActivityData.tooltip" :tooltip="lastActivityData.tooltip" />
                    </v-card-title>
                    <v-card-text style="height:calc(100% - 55px); display:flex; flex-direction: column;">
                        <div v-for="(item, index) in lastActivityData.labels" :key="'lead-type-' + index" class="mb-4">
                            <div class="d-flex justify-space-between align-center mb-1">
                                <span class="font-weight-medium">{{ item }}</span>
                                <span class="text-caption" v-if="getLastActivityTotal() > 0">{{ lastActivityData.datasets[0].data[index] }} | {{ Math.round((lastActivityData.datasets[0].data[index] / getLastActivityTotal()) * 100) }}%</span>
                                <span class="text-caption" v-else>{{ lastActivityData.datasets[0].data[index] }} | 0 %</span>
                            </div>
                            <v-progress-linear
                                :value="(getLastActivityTotal() > 0 ? ((lastActivityData.datasets[0].data[index] / getLastActivityTotal()) * 100) : 0)"
                                height="8"
                                color="primary"
                                background-color="grey lighten-3"
                                rounded
                            ></v-progress-linear>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>


        <!-- Section inférieure avec 6 composants -->
        <v-row>
            <!-- Leads par source - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :tooltip="{title: 'input_channel', description: 'input_channel_description'}" :to_trad="true" :stats="instantData" dataKey="INPUT_CHANNEL" class="fill-height-2" />
            </v-col>

            <!-- Leads par source - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="ENTITY" class="fill-height-2" />
            </v-col>
            
            <!-- Leads par source - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="CONTACT_TYPE" class="fill-height-2" />
            </v-col>
            
            <!-- Leads par source - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="BRAND" class="fill-height-2" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import GenericDataService from '@/services/GenericDataService';
import vDaterangePicker from '@/components/forms/inputs/v-daterange-picker.vue';
import statisticsDonutComponent from '@/modules/statistics/components/statisticsDonutComponent.vue'
import StatisticsTableComponent from '@/modules/statistics/components/StatisticsTableComponent.vue'
import BarComponent from '@/components/graphs/BarComponent'
import heatMapComponent from '@/modules/statistics/components/heatMapComponent.vue'
import statisticsHorizontalBarComponent from './statisticsHorizontalBarComponent.vue';
import infoTooltip from '@/components/infoTooltip';
import VerticalBarStackedComponent from '@/components/graphs/VerticalBarStackedComponent'
export default {
    name: "StatisticsMarketingDatabase",
    components: {
        statisticsDonutComponent,
        StatisticsTableComponent,
        BarComponent,
        vDaterangePicker,
        heatMapComponent,
        infoTooltip,
        statisticsHorizontalBarComponent,
        VerticalBarStackedComponent
    },
    props: ['loadData', 'prefilter', 'mode'],
    data() {
        return {
            graphKey: 0,
            filters: {},
            instantData: {},
            instantDataTmp: {
                "INPUT_CHANNEL": {
                    "Connexion RCU/CRM": 50,
                    "Import fichier": 25,
                    "Saisie manuelle": 15,
                    "Acquisition marketing": 10
                },
                "ENTITY": {
                    "Mercedes Tours": 50,
                    "Ford Alençon": 25,
                    "Opel Le Mans": 15,
                    "Suzuki Le Mans": 10
                },
                "CONTACT_TYPE": {
                    "Prospect": 50,
                    "Client": 25,
                    "Partenaires": 15,
                    "Ancien client": 10
                },
                "BRAND": {
                    "Opel": 50,
                    "Mercedes": 25,
                    "Ford": 15,
                    "Suzuki": 10
                }
            },
            kpiData: [],
            joignabilityData: [],
            marketingPressureData: [],
            b2bB2cData: {},
            lastActivityData: {},
            dataHeatMap:[],
            optinData:{}
        }
    },
    methods: {
        initFilters() {
            GenericDataService.getData('/stats/getStatisticsMarketingDatatabaseFilters' ).then((response)=> {
                this.filters = response.data.data;

                this.$store.dispatch('SET_LOADER_DASHBOARD', false);

                this.initInstantData();
            });
        },
        initInstantData() {
            this.instantData = {};
            let filtersPost = {
                filters: this.filters
            }
            if(this.prefilter)
                filtersPost.prefilters = this.prefilter

            GenericDataService.postData('/stats/getStatisticsMarketingDatabase', filtersPost).then((response)=> {
                this.instantData = response.data.data.instantData;
                this.kpiData = response.data.data.kpiData;
                this.b2bB2cData = response.data.data.b2bB2cData;
                this.joignabilityData = response.data.data.joignabilityData;
                this.lastActivityData = response.data.data.lastActivityData;
                this.marketingPressureData = response.data.data.marketingPressureData;
                this.optinData = response.data.data.consentsByChannelsData
                this.graphKey++
                setTimeout(() => {
                    this.graphKey++
                }, 1000);
                GenericDataService.postData('contact/getContactCountByCodePostal', filtersPost).then((response) => {
                    this.dataHeatMap = response.data.data;
                })
            });
        },
        getJoignabilityTotal() {
            return this.kpiData[0].value;
        },
        getMarketingPressureTotal() {
            return this.marketingPressureData.datasets[0].data.reduce((a, b) => a + b, 0);
        },
        getB2bB2cTotal() {
            return this.b2bB2cData.datasets[0].data.reduce((a, b) => a + b, 0);
        },
        getLastActivityTotal() {
            return this.lastActivityData.datasets[0].data.reduce((a, b) => a + b, 0);
        },
        reloadData() {
            this.initInstantData();
        },
    },
    created() {
        this.$store.dispatch('SET_LOADER_DASHBOARD', true).then((e) => {
            this.initFilters();
        });
    },
    watch: {
        // loadData: {
        //     handler() {
        //         if(this.loadData && this.dataHeatMap.length == 0) {
        //             GenericDataService.postData('contact/getContactCountByCodePostal', { filters: this.filters }).then((response) => {
        //                 this.dataHeatMap = response.data.data;
        //             })
        //         }
        //     },
        //     immediate: true
        // }
    }
}
</script>

<style lang="scss" scoped>
.StatisticsMarketingDatabase {
    .kpi-card {
        height: 100%;

        &.gradient-card {
            background: linear-gradient(118deg, #ffb46f 0%, #ff2b87 56%, #ff3ebb 100%) !important;
            color: white !important;
            
            .v-card-text {
                color: white !important;
            }
            
            .kpi-title {
                color: white !important;
            }
            
            .kpi-value {
                color: white !important;
                font-size: 2.5rem !important;
            }
            
            .kpi-cost-text {
                color: white !important;
            }
            
            .kpi-info-icon {
                color: white !important;
            }
            
            .kpi-icon-circle {
                background-color: rgba(255, 255, 255, 0.2) !important;
            }
        }

        .kpi-content {
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .kpi-title-container {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;
        }

        .kpi-number {
            margin-bottom: 16px;
        }

        .kpi-value {
            font-size: 2rem;
            font-weight: bold;
            margin: 0;
            line-height: 1;
            color: #333;
        }

        .kpi-cost {
            margin-bottom: 16px;
        }

        .kpi-cost-text {
            font-size: 0.875rem;
            font-weight: 700;
            color: #4caf50;
        }

        .kpi-icon-container {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
            .v-icon {
                z-index:1;
            }
        }
        .kpi-icon-container-inner{
            position: relative;
            display:flex;
            align-items: center;
            justify-content: center;
            width: 56px;
            height: 56px;
        }
        .kpi-icon-circle {
            position: absolute;
            top:0;
            z-index:0;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background-color: var(--v-primary-base) !important;
            opacity: 0.2;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fill-height-2 {
        width: 100% !important;
    }
}
</style>
